import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment
} from '@mui/material';
import { SecuritySettings } from '@/types/company-config';

interface SecuritySettingsTabProps {
  data: SecuritySettings;
  onChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
}

const SecuritySettingsTab: React.FC<SecuritySettingsTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.checked);
  };

  const handleNumberChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    onChange(field, value);
  };

  const handlePasswordPolicyChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : 
                  event.target.type === 'number' ? parseInt(event.target.value) || 0 : 
                  event.target.value;
    
    onChange('passwordPolicy', {
      ...data.passwordPolicy,
      [field]: value
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Security Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure security policies and requirements for your platform.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={data.mfaRequired ?? true}
                onChange={handleSwitchChange('mfaRequired')}
                disabled={readOnly}
              />
            }
            label="Require Multi-Factor Authentication (MFA)"
          />
          <Typography variant="body2" color="text.secondary">
            When enabled, all users must set up MFA to access the platform.
          </Typography>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Session Timeout"
            type="number"
            value={data.sessionTimeout || 60}
            onChange={handleNumberChange('sessionTimeout')}
            disabled={readOnly}
            InputProps={{
              endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
            }}
            helperText="How long before inactive sessions expire (15-480 minutes)"
            inputProps={{ min: 15, max: 480 }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Password Policy
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Minimum Password Length"
            type="number"
            value={data.passwordPolicy?.minLength || 12}
            onChange={handlePasswordPolicyChange('minLength')}
            disabled={readOnly}
            helperText="Minimum 8 characters"
            inputProps={{ min: 8, max: 128 }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Password Expiration"
            type="number"
            value={data.passwordPolicy?.passwordExpiration || 90}
            onChange={handlePasswordPolicyChange('passwordExpiration')}
            disabled={readOnly}
            InputProps={{
              endAdornment: <InputAdornment position="end">days</InputAdornment>,
            }}
            helperText="0 = never expires"
            inputProps={{ min: 0, max: 365 }}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" gutterBottom>
            Password Requirements:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={data.passwordPolicy?.requireUppercase ?? true}
                  onChange={handlePasswordPolicyChange('requireUppercase')}
                  disabled={readOnly}
                  size="small"
                />
              }
              label="Uppercase letters"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={data.passwordPolicy?.requireLowercase ?? true}
                  onChange={handlePasswordPolicyChange('requireLowercase')}
                  disabled={readOnly}
                  size="small"
                />
              }
              label="Lowercase letters"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={data.passwordPolicy?.requireNumbers ?? true}
                  onChange={handlePasswordPolicyChange('requireNumbers')}
                  disabled={readOnly}
                  size="small"
                />
              }
              label="Numbers"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={data.passwordPolicy?.requireSpecialChars ?? true}
                  onChange={handlePasswordPolicyChange('requireSpecialChars')}
                  disabled={readOnly}
                  size="small"
                />
              }
              label="Special characters"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SecuritySettingsTab;